import { memo, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Ship } from 'lucide-react'

type FeedItem = {
  id: string
  category: string
  title: string
  body: string
  time: string
  severity: string
  lat: number
  lon: number
  url?: string
  image?: string
  telemetry?: {
    confidence?: string
    sourceCount?: number
    corroboration?: string
    sourceClass?: string
    maturity?: string
    freshnessLabel?: string
  }
  relatedVideos?: Array<{
    title: string
    url: string
    thumbnailUrl?: string
    channelTitle?: string
    source?: string
  }>
}

type Props = {
  cls: (...parts: Array<string | false | null | undefined>) => string
  fmtTime: (raw: string) => string
  CATEGORY_ICON: Record<string, string>
  snapshot: any
  viewBounds: any
  visibleFeed: FeedItem[]
  displayFeed: FeedItem[]
  selectedId: string | null
  setSelectedId?: (id: string | null) => void
  setActiveFeedItem: (item: FeedItem | null) => void
  activeFeedItem: FeedItem | null
  flyTo: (lat: number, lon: number, altM?: number) => void
  onSelectFeedItem: (item: FeedItem) => void
  setActiveVideo: (video: any) => void
  pressureSeries?: any
  pressureDetail?: any
  activePressureCategory?: string
  ViewErrorBoundary: React.ComponentType<{ label: string; children: React.ReactNode }>
}

type IncidentTimelineEntry = {
  id: string
  summary: string
  note?: string
  actor?: string
  createdAt: string
}

type IncidentRecord = {
  id: string
  feedId: string
  title: string
  severity: string
  status: 'open' | 'in-review' | 'validated'
  assignedAnalyst: string
  validationRequested: boolean
  caseId?: string
  notes?: string
  timeline?: IncidentTimelineEntry[]
  createdAt: string
  lastUpdatedAt: string
}

async function fetchIncidentState() {
  const response = await fetch('/api/incidents')
  if (!response.ok) throw new Error('Failed to load incidents')
  return response.json()
}

async function createIncident(payload: Partial<IncidentRecord>) {
  const response = await fetch('/api/incidents', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!response.ok) throw new Error('Failed to create incident')
  return response.json()
}

async function updateIncident(id: string, payload: Partial<IncidentRecord>) {
  const response = await fetch(`/api/incidents/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!response.ok) throw new Error('Failed to update incident')
  return response.json()
}

async function createCaseRecord(caseId: string) {
  const response = await fetch('/api/cases', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ caseId }),
  })
  if (!response.ok) throw new Error('Failed to create case record')
  return response.json()
}

function WorldviewTelemetryRail(props: Props) {
  const {
    cls,
    fmtTime,
    CATEGORY_ICON,
    snapshot,
    viewBounds,
    visibleFeed,
    displayFeed,
    selectedId,
    setActiveFeedItem,
    activeFeedItem,
    flyTo,
    setSelectedId,
    onSelectFeedItem,
    setActiveVideo,
    pressureSeries,
    pressureDetail,
    activePressureCategory,
    ViewErrorBoundary,
  } = props

  const geoCount = useMemo(
    () => (Array.isArray(snapshot?.newsGeoMarkers) ? snapshot.newsGeoMarkers : []).filter((m: any) => m.expiresAt > Date.now()).length,
    [snapshot?.newsGeoMarkers],
  )

  const [incidents, setIncidents] = useState<IncidentRecord[]>([])
  const [cases, setCases] = useState<string[]>([])
  const [analysts, setAnalysts] = useState<Array<{ id: string; name: string; active?: boolean }>>([])
  const [caseInput, setCaseInput] = useState('')
  const [incidentNote, setIncidentNote] = useState('')

  useEffect(() => {
    fetchIncidentState()
      .then(data => {
        setIncidents(Array.isArray(data?.incidents) ? data.incidents : [])
        setCases(Array.isArray(data?.cases) ? data.cases : [])
        setAnalysts(Array.isArray(data?.analysts) ? data.analysts : [])
      })
      .catch(() => {})
  }, [])

  const activeIncident = useMemo(
    () => (activeFeedItem ? incidents.find(i => i.feedId === activeFeedItem.id) ?? null : null),
    [activeFeedItem, incidents],
  )

  const upsertIncident = async (next: IncidentRecord) => {
    const data = await updateIncident(next.id, next)
    setIncidents(current => current.map(i => (i.id === next.id ? data.incident : i)))
    setCases(Array.isArray(data?.cases) ? data.cases : cases)
    return data.incident as IncidentRecord
  }

  const createIncidentFromFeed = async (item: FeedItem) => {
    const existing = incidents.find(i => i.feedId === item.id)
    if (existing) return existing
    const now = new Date().toISOString()
    const data = await createIncident({
      feedId: item.id,
      title: item.title,
      severity: item.severity || 'medium',
      status: 'open',
      assignedAnalyst: 'Unassigned',
      validationRequested: false,
      notes: '',
      timeline: [
        {
          id: `timeline-${Date.now()}`,
          summary: 'Incident created',
          note: 'Created from worldview event detail.',
          actor: 'system',
          createdAt: now,
        },
      ],
    })
    setIncidents(current => [data.incident, ...current])
    setCases(Array.isArray(data?.cases) ? data.cases : cases)
    return data.incident as IncidentRecord
  }

  return (
    <aside className="right-rail worldview-telemetry-rail worldview-telemetry-rail-minimal">
      <ViewErrorBoundary label="Worldview Telemetry Rail">
        <section className="panel feed-panel worldview-telemetry-panel worldview-feed-panel-condensed">
          <div className="panel-header">
            <AlertTriangle size={16} />
            <span>Event Feed</span>
            {geoCount > 0 && (
              <span className="geo-marker-chip" title="LLM geo-located news events active on map (24h expiry)">
                ◉ {geoCount} GEO
              </span>
            )}
            {viewBounds && visibleFeed.length > 0 && (<span className="muted" style={{ marginLeft: 'auto', fontSize: 11 }}>in view</span>)}
          </div>
          <div className="feed-list" style={{ overflowY: 'auto', maxHeight: 380, scrollbarWidth: 'thin', scrollbarColor: '#2a3f55 transparent' }}>
            {displayFeed.slice(0, 50).map(item => (
              <button
                key={item.id}
                className={cls('feed-item', item.category === 'GeoEvent' && 'geo-event', item.id === selectedId && 'active')}
                onClick={() => onSelectFeedItem(item)}
              >
                {item.image && (
                  <div style={{ marginBottom: 8, borderRadius: 10, overflow: 'hidden', background: 'rgba(255,255,255,0.03)' }}>
                    <img src={item.image} alt={item.title} loading="lazy" style={{ width: '100%', height: 108, objectFit: 'cover', display: 'block', filter: 'saturate(0.88) contrast(1.04)' }} />
                  </div>
                )}
                <div className="feed-top">
                  <strong style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ marginRight: 5, opacity: 0.8, color: item.category === 'GeoEvent' ? '#aa88ff' : undefined }}>
                      {CATEGORY_ICON[item.category] ?? '·'}
                    </span>
                    <span>{item.title}</span>
                    {item.category === 'GeoEvent' && (
                      <span className="geo-event-badge">◉ GEO·LOCATED</span>
                    )}
                    {Array.isArray(item.relatedVideos) && item.relatedVideos.length > 0 && (
                      <span className="youtube-feed-badge" title="Has related video">▶ YouTube</span>
                    )}
                  </strong>
                  <span style={{ fontSize: 10, opacity: 0.7, whiteSpace: 'nowrap', marginLeft: 6 }}>{fmtTime(item.time)}</span>
                </div>
                <div className="muted" style={{ fontSize: 11 }}>{item.body}</div>
                {item.telemetry && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    <span className="status-tag">{item.telemetry.maturity || 'raw'}</span>
                    <span className="status-tag">{item.telemetry.confidence || 'n/a'} confidence</span>
                    <span className="status-tag">{item.telemetry.corroboration || 'unconfirmed'}</span>
                    <span className="status-tag">{item.telemetry.sourceClass || 'source'}</span>
                    <span className="status-tag">{item.telemetry.sourceCount || 1} src</span>
                    <span className="status-tag">{item.telemetry.freshnessLabel || 'recent'}</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        </section>

        {activeFeedItem && (
          <div className="rdrawer-backdrop" onClick={() => setActiveFeedItem(null)}>
            <div className="rdrawer" style={{ width: 'min(720px, 92vw)', height: 'min(560px, 78vh)', left: '50%', top: '52%', transform: 'translate(-50%, -50%)', position: 'fixed' }} onClick={e => e.stopPropagation()}>
              <div className="rdrawer-titlebar">
                <span className="rdrawer-title">Event Media · {activeFeedItem.title}</span>
                <button className="rdrawer-close" onClick={() => setActiveFeedItem(null)}>×</button>
              </div>
              <div className="rdrawer-body" style={{ padding: 16 }}>
                <div className="entity-card" style={{ marginTop: 0 }}>
                  {activeFeedItem.image && (
                    <div style={{ margin: '-16px -16px 14px', maxHeight: 220, overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <img src={activeFeedItem.image} alt={activeFeedItem.title} style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }} />
                    </div>
                  )}
                  <div className="eyebrow" style={{ marginBottom: 6 }}>{activeFeedItem.category}</div>
                  <h3>{activeFeedItem.title}</h3>
                  <p>{activeFeedItem.body}</p>
                  <div className="kv"><span>Time</span><strong>{fmtTime(activeFeedItem.time)}</strong></div>
                  <div className="kv"><span>Severity</span><strong>{activeFeedItem.severity}</strong></div>
                  {activeFeedItem.telemetry?.maturity && <div className="kv"><span>Maturity</span><strong>{activeFeedItem.telemetry.maturity}</strong></div>}
                  {activeFeedItem.telemetry?.confidence && <div className="kv"><span>Confidence</span><strong>{activeFeedItem.telemetry.confidence}</strong></div>}
                  {activeFeedItem.telemetry?.corroboration && <div className="kv"><span>Corroboration</span><strong>{activeFeedItem.telemetry.corroboration}</strong></div>}
                  {activeFeedItem.telemetry?.sourceClass && <div className="kv"><span>Source Class</span><strong>{activeFeedItem.telemetry.sourceClass}</strong></div>}
                  {activeFeedItem.telemetry?.sourceCount != null && <div className="kv"><span>Source Count</span><strong>{activeFeedItem.telemetry.sourceCount}</strong></div>}
                  {activeFeedItem.telemetry?.freshnessLabel && <div className="kv"><span>Freshness</span><strong>{activeFeedItem.telemetry.freshnessLabel}</strong></div>}
                  {activeFeedItem.url && (<div style={{ marginTop: 10 }}><a href={activeFeedItem.url} target="_blank" rel="noreferrer" className="news-drawer-link">Open source article</a></div>)}

                  <div className="note-card" style={{ marginTop: 14 }}>
                    <div className="eyebrow" style={{ marginBottom: 8 }}>Incident Actions (local workflow)</div>
                    {!activeIncident ? (
                      <button
                        className="button"
                        onClick={() => { void createIncidentFromFeed(activeFeedItem) }}
                        style={{ width: '100%' }}
                      >
                        Create incident
                      </button>
                    ) : (
                      <div style={{ display: 'grid', gap: 8 }}>
                        <div className="kv"><span>Incident</span><strong>{activeIncident.id}</strong></div>
                        <div className="kv"><span>Status</span><strong>{activeIncident.status}</strong></div>
                        <div className="kv"><span>Assigned analyst</span><strong>{activeIncident.assignedAnalyst}</strong></div>
                        <div className="kv"><span>Validation</span><strong>{activeIncident.validationRequested ? 'Requested' : 'Not requested'}</strong></div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <button className="button" onClick={() => { void upsertIncident({ ...activeIncident, validationRequested: true, status: activeIncident.status === 'open' ? 'in-review' : activeIncident.status, timeline: [{ id: `timeline-${Date.now()}`, summary: 'Validation requested', note: 'Queued for analyst validation.', actor: 'analyst', createdAt: new Date().toISOString() }, ...(activeIncident.timeline || [])], lastUpdatedAt: new Date().toISOString() }) }}>Request validation</button>
                          <button className="button" onClick={() => { void upsertIncident({ ...activeIncident, status: 'validated', timeline: [{ id: `timeline-${Date.now()}`, summary: 'Incident validated', note: 'Marked validated from workflow controls.', actor: 'analyst', createdAt: new Date().toISOString() }, ...(activeIncident.timeline || [])], lastUpdatedAt: new Date().toISOString() }) }}>Mark validated</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          {(analysts.length > 0 ? analysts.slice(0, 2) : [{ id: 'fallback-a', name: 'Analyst A. Reyes' }, { id: 'fallback-b', name: 'Analyst M. Chen' }]).map(analyst => (
                            <button key={analyst.id} className="button" onClick={() => { void upsertIncident({ ...activeIncident, assignedAnalyst: analyst.name, timeline: [{ id: `timeline-${Date.now()}`, summary: 'Analyst assigned', note: `Assigned to ${analyst.name}.`, actor: 'system', createdAt: new Date().toISOString() }, ...(activeIncident.timeline || [])], lastUpdatedAt: new Date().toISOString() }) }}>{analyst.name}</button>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input
                            value={caseInput}
                            onChange={(e) => setCaseInput(e.target.value)}
                            placeholder="Case ID, e.g. CASE-2401"
                            style={{ flex: 1, background: '#07131f', color: '#cfe5f7', border: '1px solid #254159', borderRadius: 6, padding: '8px 10px' }}
                          />
                          <button
                            className="button"
                            onClick={() => {
                              const trimmed = caseInput.trim()
                              if (!trimmed) return
                              if (!cases.includes(trimmed)) {
                                void createCaseRecord(trimmed).then(data => {
                                  setCases(Array.isArray(data?.cases) ? data.cases : [trimmed, ...cases])
                                }).catch(() => {})
                              }
                              void upsertIncident({ ...activeIncident, caseId: trimmed, timeline: [{ id: `timeline-${Date.now()}`, summary: 'Added to case', note: `Linked to ${trimmed}.`, actor: 'analyst', createdAt: new Date().toISOString() }, ...(activeIncident.timeline || [])], lastUpdatedAt: new Date().toISOString() })
                            }}
                          >
                            Add to case
                          </button>
                        </div>
                        <div style={{ display: 'grid', gap: 8 }}>
                          <textarea
                            value={incidentNote}
                            onChange={(e) => setIncidentNote(e.target.value)}
                            placeholder="Add incident note"
                            rows={3}
                            style={{ width: '100%', background: '#07131f', color: '#cfe5f7', border: '1px solid #254159', borderRadius: 6, padding: '8px 10px', resize: 'vertical' }}
                          />
                          <button
                            className="button"
                            onClick={() => {
                              const trimmed = incidentNote.trim()
                              if (!trimmed) return
                              void upsertIncident({ ...activeIncident, notes: trimmed, timeline: [{ id: `timeline-${Date.now()}`, summary: 'Note added', note: trimmed, actor: 'analyst', createdAt: new Date().toISOString() }, ...(activeIncident.timeline || [])], lastUpdatedAt: new Date().toISOString() })
                              setIncidentNote('')
                            }}
                          >
                            Save note
                          </button>
                        </div>
                        {activeIncident.caseId && <div className="kv"><span>Case</span><strong>{activeIncident.caseId}</strong></div>}
                        {activeIncident.notes && <div className="kv"><span>Latest note</span><strong>{activeIncident.notes}</strong></div>}
                        {(activeIncident.timeline || []).length > 0 && (
                          <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                            <div className="eyebrow" style={{ marginBottom: 0 }}>Incident timeline</div>
                            {(activeIncident.timeline || []).slice(0, 5).map(entry => (
                              <div key={entry.id} className="mini-card">
                                <strong>{entry.summary}</strong>
                                <span>{entry.actor || 'system'} · {fmtTime(entry.createdAt)}</span>
                                {entry.note ? <span>{entry.note}</span> : null}
                              </div>
                            ))}
                          </div>
                        )}
                        {activeIncident.caseId && <div className="kv"><span>Case</span><strong>{activeIncident.caseId}</strong></div>}
                      </div>
                    )}
                  </div>
                </div>

                {Array.isArray(activeFeedItem.relatedVideos) && activeFeedItem.relatedVideos.length > 0 ? (
                  <div className="video-cover-grid" style={{ marginTop: 14 }}>
                    {activeFeedItem.relatedVideos.slice(0, 6).map((video, index) => (
                      <button
                        key={`${video.url}-${index}`}
                        className="video-cover-card"
                        onClick={() => setActiveVideo(video)}
                      >
                        <div className="video-cover-thumb-shell">
                          {video.thumbnailUrl ? (
                            <img className="video-cover-thumb" src={video.thumbnailUrl} alt={video.title} loading="lazy" />
                          ) : (
                            <div className="video-cover-thumb video-cover-thumb-fallback">YT</div>
                          )}
                          <div className="video-cover-play">▶</div>
                        </div>
                        <div className="video-cover-meta">
                          <div className="eyebrow">Related video {index + 1}</div>
                          <h4>{video.title}</h4>
                          <div className="muted" style={{ fontSize: 11 }}>{video.channelTitle || video.source || 'YouTube'}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="note-card" style={{ marginTop: 14 }}>No linked video packages found for this item yet.</div>
                )}
              </div>
            </div>
          </div>
        )}

        <section className="panel feed-panel">
          <div className="panel-header"><AlertTriangle size={16} /><span>Incident Queue</span></div>
          <div className="detail-list" style={{ overflowY: 'auto', maxHeight: 200, scrollbarWidth: 'thin', scrollbarColor: '#2a3f55 transparent' }}>
            {incidents.length === 0 ? (
              <div className="mini-card"><strong>No incidents yet</strong><span>Create incidents from event detail actions.</span></div>
            ) : (
              incidents.slice(0, 8).map(incident => {
                const feed = displayFeed.find(item => item.id === incident.feedId)
                return (
                  <div key={incident.id} className="mini-card" style={{ cursor: feed ? 'pointer' : 'default' }} onClick={() => {
                    if (!feed) return
                    setSelectedId?.(feed.id)
                    onSelectFeedItem(feed)
                    flyTo(feed.lat, feed.lon, 1400000)
                  }}>
                    <strong>{incident.title}</strong>
                    <span>{incident.assignedAnalyst} · {incident.status}</span>
                    <span className="status-tag">{incident.caseId || 'uncased'}</span>
                  </div>
                )
              })
            )}
          </div>
        </section>

        <section className="panel feed-panel">
          <div className="panel-header"><AlertTriangle size={16} /><span>Pressure & Explainability</span></div>
          <div className="detail-list" style={{ overflowY: 'auto', maxHeight: 220, scrollbarWidth: 'thin', scrollbarColor: '#2a3f55 transparent' }}>
            <div className="mini-card">
              <strong>Active domain</strong>
              <span>{activePressureCategory || 'conflict'}</span>
            </div>
            {pressureSeries?.series?.length ? (() => {
              const latest = pressureSeries.series[pressureSeries.series.length - 1]
              const score = latest?.scores?.[activePressureCategory || 'conflict']
              const delta = latest?.deltas?.[activePressureCategory || 'conflict']
              const confidence = latest?.confidenceAvg
              return (
                <>
                  <div className="mini-card">
                    <strong>Current pressure</strong>
                    <span>{score ?? 'n/a'}</span>
                    <span className="status-tag">Δ {delta ?? 0}</span>
                  </div>
                  <div className="mini-card">
                    <strong>Confidence</strong>
                    <span>{typeof confidence === 'number' ? `${Math.round(confidence * 100)}%` : 'n/a'}</span>
                  </div>
                  {(latest?.topDrivers || []).slice(0, 3).map((driver: any, index: number) => (
                    <div key={`${driver.label}-${index}`} className="mini-card">
                      <strong>{driver.label}</strong>
                      <span>{driver.category}</span>
                      <span className="status-tag">impact {Math.round(driver.scoreImpact || 0)}</span>
                    </div>
                  ))}
                </>
              )
            })() : <div className="mini-card"><strong>No pressure series</strong><span>Viewport-linked pressure data will appear here.</span></div>}
            {pressureDetail?.drivers?.length > 0 && (
              <div className="mini-card">
                <strong>Why flagged</strong>
                <span>{pressureDetail.drivers[0]?.label || 'Driver detail pending'}</span>
              </div>
            )}
          </div>
        </section>

        <section className="panel feed-panel">
          <div className="panel-header"><Ship size={16} /><span>Airspace & Maritime</span></div>
          <div className="detail-list" style={{ overflowY: 'auto', maxHeight: 260, scrollbarWidth: 'thin', scrollbarColor: '#2a3f55 transparent' }}>
            {(snapshot?.notams ?? []).map((zone: any) => (
              <div key={zone.id} className="mini-card">
                <strong>{zone.name}</strong>
                <span>{zone.region}</span>
                <span className={cls('status-tag', zone.status === 'Closed' && 'danger', zone.status === 'Restricted' && 'warn')}>{zone.status}</span>
              </div>
            ))}
            {(snapshot?.maritime ?? []).map((ship: any) => (
              <div key={ship.id} className="mini-card" style={{ cursor: 'pointer' }} onClick={() => flyTo(ship.lat, ship.lon, 600000)}>
                <strong>⚓ {ship.name}</strong>
                <span>{ship.vesselType} · {ship.flag}</span>
                {ship.anomaly && <span className="status-tag warn">⚠</span>}
              </div>
            ))}
          </div>
        </section>
      </ViewErrorBoundary>
    </aside>
  )
}

export default memo(WorldviewTelemetryRail)
